import { useMutation } from '@apollo/client';
import { format } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../core/components/Button';
import DateField from '../../../../core/components/Fields/DateField';
import StringField from '../../../../core/components/Fields/StringField';
import TextField from '../../../../core/components/Fields/TextField';
import FormField from '../../../../core/components/FormField';
import { useToaster } from '../../../../core/components/Toast';
import classnames from '../../../../core/utils/classnames';
import { TEST_AGENT } from '../../queries';

const generateMessageId = () => {
    const uuid = crypto.randomUUID();
    const domain = window.location.hostname || 'example.com';
    return `<${uuid}@${domain}>`;
};

interface TestEmailFormProps {
    agentId: string;
    onSubmitted?: () => void;
    onCancel?: () => void;
    hasOptionalFieldsCollapsed?: boolean;
}

/** The email to test an agent with, entered by hand. */
const TestEmailForm = ({ agentId, onSubmitted, onCancel, hasOptionalFieldsCollapsed = false }: TestEmailFormProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { publishToast } = useToaster();
    const [isShowingOptionalFields, setShowingOptionalFields] = useState(false);
    const [date, setDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [messageId, setMessageId] = useState<string>(generateMessageId());
    const [subject, setSubject] = useState<string>('');
    const [sender, setSender] = useState<string>('');
    const [receivers, setReceivers] = useState<string>('');
    const [body, setBody] = useState<string>('');

    const [testAgent, { loading }] = useMutation(TEST_AGENT);

    const reset = () => {
        setSubject('');
        setSender('');
        setReceivers('');
        setBody('');
        setMessageId(generateMessageId());
    };

    const handleSubmit = async () => {
        if (!agentId || !body) return;

        try {
            await testAgent({
                variables: {
                    agentId,
                    data: {
                        date,
                        messageId,
                        subject,
                        sender,
                        receivers: receivers
                            .split(',')
                            .map((r) => r.trim())
                            .filter(Boolean),
                        body,
                    },
                },
            });

            publishToast({ description: t('testModal.testSuccess'), status: 'success' });
            reset();
            onSubmitted?.();
        } catch (error) {
            console.error('Test agent error:', error);
            publishToast({ description: t('testModal.testError'), status: 'error' });
        }
    };

    const handleCancel = () => {
        reset();
        onCancel?.();
    };

    const dateField = (
        <FormField label={t('testModal.dateLabel')}>
            <DateField value={date} onValueChange={setDate} />
        </FormField>
    );
    const senderField = (
        <FormField label={t('testModal.senderLabel')}>
            <StringField value={sender} onValueChange={setSender} placeholder={t('testModal.senderPlaceholder')} />
        </FormField>
    );
    const receiversField = (
        <FormField label={t('testModal.receiversLabel')}>
            <StringField
                value={receivers}
                onValueChange={setReceivers}
                placeholder={t('testModal.receiversPlaceholder')}
            />
        </FormField>
    );
    const subjectField = (
        <FormField label={t('testModal.subjectLabel')}>
            <StringField value={subject} onValueChange={setSubject} placeholder={t('testModal.subjectPlaceholder')} />
        </FormField>
    );
    const bodyField = (
        <FormField label={t('testModal.bodyLabel')} required>
            <TextField
                value={body}
                onValueChange={setBody}
                placeholder={t('testModal.bodyPlaceholder')}
                rows={4}
                required
            />
        </FormField>
    );
    const messageIdField = (
        <FormField label={t('testModal.messageIdLabel')}>
            <StringField
                value={messageId}
                onValueChange={setMessageId}
                placeholder={t('testModal.messageIdPlaceholder')}
            />
        </FormField>
    );

    return (
        <div className="flex flex-col gap-4">
            {hasOptionalFieldsCollapsed ? (
                <>
                    {senderField}
                    {subjectField}
                    {bodyField}
                    <button
                        type="button"
                        aria-expanded={isShowingOptionalFields}
                        onClick={() => setShowingOptionalFields(!isShowingOptionalFields)}
                        className="self-start cursor-pointer rounded border-none bg-transparent p-0 text-left text-sm font-medium text-content-brand-default hover:text-content-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
                    >
                        {isShowingOptionalFields
                            ? t('testModal.hideOptionalFields')
                            : t('testModal.showOptionalFields')}
                    </button>
                    {isShowingOptionalFields && (
                        <>
                            {dateField}
                            {receiversField}
                            {messageIdField}
                        </>
                    )}
                </>
            ) : (
                <>
                    {dateField}
                    {senderField}
                    {receiversField}
                    {subjectField}
                    {bodyField}
                    {messageIdField}
                </>
            )}

            {/* in the drawer Run test sits under the fields on the left, as in Figma; the modal keeps it on the right */}
            <div className={classnames('flex gap-2', hasOptionalFieldsCollapsed ? 'justify-start' : 'justify-end')}>
                {onCancel && (
                    <Button variant="ghost" onClick={handleCancel}>
                        {t('overview.cancel')}
                    </Button>
                )}
                <Button variant="primary" onClick={handleSubmit} disabled={!agentId || !body} loading={loading}>
                    {loading ? t('overview.testing') : t('overview.runTest')}
                </Button>
            </div>
        </div>
    );
};

export default TestEmailForm;
